import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const ISSUE_TYPE_LABELS = {
  not_starting: "Won't start charging",
  stopped_unexpectedly: 'Stopped unexpectedly',
  payment_issue: 'Payment problem',
  physical_damage: 'Physical damage',
  cable_connector: 'Cable / connector issue',
  other: 'Something else',
};

const statusColors = {
  open: { background: '#FEF2F2', color: '#DC2626' },
  investigating: { background: '#FFFBEB', color: '#D97706' },
  resolved: { background: '#F0FDF4', color: '#16A34A' },
};

export default function ChargerIssuesPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('open');
  const [updatingId, setUpdatingId] = useState(null);

  const authHeaders = { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` };

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      const res = await fetch(`${API}/api/admin/charger-issues?${params}`, { headers: authHeaders });
      const data = await res.json();
      setReports(data.reports || []);
    } catch (err) {
      console.error('Failed to fetch charger issues:', err);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await fetch(`${API}/api/admin/charger-issues/${id}`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      await fetchReports();
    } catch (err) {
      console.error('Failed to update issue status:', err);
    }
    setUpdatingId(null);
  };

  return (
    <div style={{ padding: '32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Charger Issues</h1>
          <p style={{ color: '#6B7280', fontSize: 14, marginTop: 4 }}>{reports.length} report{reports.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {['open', 'investigating', 'resolved', ''].map(s => (
          <button
            key={s || 'all'}
            onClick={() => setFilterStatus(s)}
            style={{
              padding: '8px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
              border: filterStatus === s ? 'none' : '1.5px solid #E5E7EB',
              background: filterStatus === s ? '#111827' : 'white',
              color: filterStatus === s ? 'white' : '#374151',
            }}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
          </button>
        ))}
      </div>

      <div style={{ background: 'white', borderRadius: 16, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              <th style={thStyle}>Charger</th>
              <th style={thStyle}>Issue</th>
              <th style={thStyle}>Description</th>
              <th style={thStyle}>Reported By</th>
              <th style={thStyle}>Reported</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading...</td></tr>
            ) : reports.length === 0 ? (
              <tr><td colSpan="7" style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>No reports found</td></tr>
            ) : (
              reports.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                  <td style={tdStyle}>
                    {r.charger_name}
                    <div style={{ fontSize: 12, color: '#9CA3AF', fontFamily: 'monospace' }}>{r.serial_number}</div>
                  </td>
                  <td style={tdStyle}>{ISSUE_TYPE_LABELS[r.issue_type] || r.issue_type}</td>
                  <td style={{ ...tdStyle, maxWidth: 260, color: '#6B7280' }}>{r.description || '—'}</td>
                  <td style={tdStyle}>
                    {r.reporter_first_name ? `${r.reporter_first_name} ${r.reporter_last_name}` : '—'}
                    {r.reporter_email && <div style={{ fontSize: 12, color: '#9CA3AF' }}>{r.reporter_email}</div>}
                  </td>
                  <td style={tdStyle}>{new Date(r.created_at).toLocaleString('en-GB')}</td>
                  <td style={tdStyle}>
                    <span style={{ ...badgeBase, ...(statusColors[r.status] || statusColors.open) }}>{r.status}</span>
                  </td>
                  <td style={tdStyle}>
                    {r.status !== 'resolved' && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        {r.status === 'open' && (
                          <button
                            onClick={() => updateStatus(r.id, 'investigating')}
                            disabled={updatingId === r.id}
                            style={smallBtnStyle}
                          >
                            Investigating
                          </button>
                        )}
                        <button
                          onClick={() => updateStatus(r.id, 'resolved')}
                          disabled={updatingId === r.id}
                          style={{ ...smallBtnStyle, background: '#10B981', color: 'white', border: 'none' }}
                        >
                          Resolve
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tdStyle = { padding: '14px 16px', fontSize: 14, verticalAlign: 'top' };
const badgeBase = { padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' };
const smallBtnStyle = { padding: '6px 12px', border: '1px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
